import React, { useCallback, useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import PropTypes from 'prop-types';

import ButtonSp from '../../components/ButtonSp';

import InputTextSp from '../../components/InputTextSp';
import LabelSp from '../../components/LabelSp';
import { showMessage } from '../../components/MessageDialog';

import AtualizacaoService from '../../services/AtualizacaoService';

import { errorHandle, validateFields } from '../../util/functions';
import { AtualizacaoModel } from '../../util/Models';
import { StateScreen } from '../constants';
import DropdownSp from '../../components/DropdownSp';
import AtualizacaoCrudLiberacao from './crudLiberacao';
import AtualizacaoCrudEvento from './crudEvento';

// Publicação de versão (upload do ZIP do pacote) + liberações (quem recebe) +
// eventos (telemetria da frota). O zip é imutável: corrigiu algo = nova versão.
function AtualizacaoCrud(props) {
  AtualizacaoCrud.propTypes = {
    stateScreen: PropTypes.string.isRequired,
    idSelected: PropTypes.number.isRequired,
    onClose: PropTypes.func.isRequired,
  };

  const { stateScreen, idSelected, onClose } = props;

  // states
  const [atualizacao, setAtualizacao] = useState(new AtualizacaoModel());

  const [errorLoadRecord, setErrorLoadRecord] = useState(false);

  // useCallbacks
  const loadRecord = useCallback(async _id => {
    try {
      const retorno = await AtualizacaoService.findById(_id);

      setAtualizacao(retorno);

      setErrorLoadRecord(false);
    } catch (err) {
      setErrorLoadRecord(true);
      errorHandle(err);
    }
  }, []);

  // funcoes
  function viewMode() {
    return stateScreen === StateScreen.stView || errorLoadRecord;
  }

  function insertMode() {
    return stateScreen === StateScreen.stInsert;
  }

  function handleBack() {
    if (stateScreen === StateScreen.stView) {
      onClose();
    } else {
      showMessage('Confirmação', 'Abandonar mudanças?', idx => {
        if (idx === 1) {
          onClose();
        }
      });
    }
  }

  function handleSave() {
    if (stateScreen === StateScreen.stView) {
      onClose();
    } else {
      salvarRecord();
    }
  }

  async function salvarRecord() {
    try {
      let retorno;
      if (stateScreen === StateScreen.stInsert) {
        retorno = await AtualizacaoService.insert(atualizacao);
        toast.success('Versão publicada. Lembre: publicar não libera — crie as liberações.');
      } else {
        retorno = await AtualizacaoService.update(atualizacao);
        toast.success('Registro salvo com sucesso.');
      }
      onClose(retorno);
    } catch (err) {
      errorHandle(err);
    }
  }

  // useEffects
  useEffect(() => {
    if (stateScreen === StateScreen.stUpdate || stateScreen === StateScreen.stView) {
      loadRecord(idSelected);
    } else if (stateScreen === StateScreen.stInsert) {
      const novo = new AtualizacaoModel();

      setAtualizacao(novo);
    }
  }, [loadRecord, idSelected, stateScreen]);

  // render principal
  return (
    <>
      <div className="p-col-6 p-sm-3 p-lg-2 p-fluid">
        <LabelSp>Versão</LabelSp>
        <InputTextSp
          autoFocus
          value={atualizacao.versao}
          maxLength={20}
          required
          placeholder="10.9.0"
          disabled={!insertMode()}
          onChange={e => {
            setAtualizacao({ ...atualizacao, versao: e.target.value });
          }}
        />
      </div>

      {insertMode() ? (
        <div className="p-col-6 p-sm-5 p-lg-4 p-fluid">
          <LabelSp>Pacote (ZIP)</LabelSp>
          <InputTextSp
            type="file"
            accept=".zip"
            required
            onChange={e => {
              const file = e.target.files && e.target.files[0] ? e.target.files[0] : undefined;
              setAtualizacao({ ...atualizacao, file });
            }}
          />
        </div>
      ) : (
        <>
          <div className="p-col-6 p-sm-3 p-lg-2 p-fluid">
            <LabelSp>Tamanho</LabelSp>
            <InputTextSp
              value={`${(Number(atualizacao.tamanho || 0) / 1024 / 1024).toFixed(1)} MB`}
              disabled
            />
          </div>
          <div className="p-col-6 p-sm-3 p-lg-2 p-fluid">
            <LabelSp>Ativa</LabelSp>
            <DropdownSp
              options={[
                { label: 'Sim', value: true },
                { label: 'Não', value: false },
              ]}
              disabled={viewMode()}
              onChange={e => {
                setAtualizacao({ ...atualizacao, ativo: e.target.value });
              }}
              value={atualizacao.ativo}
            />
          </div>
          <div className="p-col-12 p-sm-6 p-lg-6 p-fluid">
            <LabelSp>SHA-256 do pacote</LabelSp>
            <InputTextSp value={atualizacao.sha256 || ''} disabled />
          </div>
        </>
      )}

      <div className="p-col-12 p-fluid">
        <LabelSp>Notas da versão</LabelSp>
        <InputTextSp
          value={atualizacao.notas || ''}
          maxLength={500}
          disabled={viewMode()}
          onChange={e => {
            setAtualizacao({ ...atualizacao, notas: e.target.value });
          }}
        />
      </div>

      <div className="p-col-12 p-lg-12" style={{ textAlign: 'start' }}>
        {!viewMode() ? (
          <ButtonSp
            className="p-button-success"
            icon="pi pi-save"
            label={insertMode() ? 'Publicar' : 'Salvar'}
            keyAccess="S"
            disabled={
              !validateFields(atualizacao, ['versao']) || (insertMode() && !atualizacao.file)
            }
            showConfirmation
            onClick={handleSave}
          />
        ) : null}
        <ButtonSp
          className="p-button-secondary"
          label="Voltar"
          icon="pi pi-chevron-circle-left"
          keyAccess="V"
          onClick={handleBack}
        />
      </div>

      {!insertMode() && atualizacao.id ? (
        <>
          <AtualizacaoCrudLiberacao idVersao={atualizacao.id} viewMode={viewMode()} />
          <AtualizacaoCrudEvento versao={atualizacao.versao} />
        </>
      ) : null}
    </>
  );
}

export default AtualizacaoCrud;
